/**
 * Filter operator constants and utilities
 * Matches backend filter operator support
 */
import i18n from '../i18n';

// Operator string constants
export const FILTER_OPERATORS = {
  IN: 'in',
  NOT_IN: 'not_in',
  EQ: 'eq',
  NEQ: 'neq',
  ILIKE: 'ilike',
  LIKE: 'like',
  STARTS_WITH: 'starts_with',
  NOT_STARTS_WITH: 'not_starts_with',
  ENDS_WITH: 'ends_with',
  NOT_ENDS_WITH: 'not_ends_with',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  NOT_ILIKE: 'not_ilike',
  IS_NULL: 'is_null',
  GT: 'gt',
  LT: 'lt',
  GTE: 'gte',
  LTE: 'lte',
};

// Human-readable labels for operators
export const OPERATOR_LABELS = {
  [FILTER_OPERATORS.IN]: 'is',
  [FILTER_OPERATORS.NOT_IN]: 'is not',
  [FILTER_OPERATORS.EQ]: 'equals',
  [FILTER_OPERATORS.NEQ]: 'not equals',
  [FILTER_OPERATORS.ILIKE]: 'contains',
  [FILTER_OPERATORS.LIKE]: 'matches',
  [FILTER_OPERATORS.STARTS_WITH]: 'starts with',
  [FILTER_OPERATORS.NOT_STARTS_WITH]: 'does not start with',
  [FILTER_OPERATORS.ENDS_WITH]: 'ends with',
  [FILTER_OPERATORS.NOT_ENDS_WITH]: 'does not end with',
  [FILTER_OPERATORS.CONTAINS]: 'contains (exact)',
  [FILTER_OPERATORS.NOT_CONTAINS]: 'does not contain',
  [FILTER_OPERATORS.NOT_ILIKE]: 'does not contain',
  [FILTER_OPERATORS.IS_NULL]: 'is empty',
  [FILTER_OPERATORS.GT]: 'greater than',
  [FILTER_OPERATORS.LT]: 'less than',
  [FILTER_OPERATORS.GTE]: 'equal or more than',
  [FILTER_OPERATORS.LTE]: 'equal or less than',
};

// Date-specific operator labels (Linear-style)
export const DATE_OPERATOR_LABELS = {
  [FILTER_OPERATORS.GT]: 'after',
  [FILTER_OPERATORS.LT]: 'before',
  [FILTER_OPERATORS.GTE]: 'after',
  [FILTER_OPERATORS.LTE]: 'before',
};

// Default operator when adding a filter, by columnType
export const DEFAULT_OPERATOR_BY_TYPE = {
  id: FILTER_OPERATORS.IN,
  string: FILTER_OPERATORS.IN,
  numeric: FILTER_OPERATORS.GTE,
  date: FILTER_OPERATORS.GTE,
  datetime: FILTER_OPERATORS.GTE,
};

/**
 * Check if a filter definition is a checkbox type (has selectable options)
 */
export const isCheckboxFilterType = (filterDef) => {
  if (!filterDef) return false;
  const hasOptions = filterDef.options && filterDef.options.length > 0;
  const { columnType } = filterDef;
  return hasOptions && (columnType === 'string' || columnType === 'id' || !columnType);
};

/**
 * Translate an operator key for display. Uses i18n if a translation exists,
 * otherwise falls back to the English labels above.
 */
const tOp = (op, isDate = false) => {
  const englishFallback = (isDate && DATE_OPERATOR_LABELS[op]) || OPERATOR_LABELS[op] || op;
  if (isDate) {
    return i18n.t(`filters:operators.date.${op}`, {
      defaultValue: i18n.t(`filters:operators.${op}`, { defaultValue: englishFallback }),
    });
  }
  return i18n.t(`filters:operators.${op}`, { defaultValue: englishFallback });
};

/**
 * Filters whose chip also offers "is empty". Only Goal, on purpose: "No goal"
 * is the view a goal is built out of, while an empty category or account
 * already has its own alert.
 */
const EMPTY_CHOICE_ENTITIES = ['Goal'];

/**
 * Get available operators for the chip-level operator dropdown.
 * Returns operators for all filter types so every chip can be segmented.
 */
export const getAvailableOperators = (filterDef) => {
  if (!filterDef) return [];
  const { columnType } = filterDef;
  const hasOptions = filterDef.options && filterDef.options.length > 0;

  // Checkbox type (string/id with selectable options)
  if (hasOptions && (columnType === 'string' || columnType === 'id' || !columnType)) {
    const ops = [
      { value: FILTER_OPERATORS.IN, label: tOp(FILTER_OPERATORS.IN) },
      { value: FILTER_OPERATORS.NOT_IN, label: tOp(FILTER_OPERATORS.NOT_IN) },
    ];
    if (
      EMPTY_CHOICE_ENTITIES.includes(filterDef.entity)
      && (filterDef.supportedOperators || []).includes(FILTER_OPERATORS.IS_NULL)
    ) {
      ops.push({ value: FILTER_OPERATORS.IS_NULL, label: tOp(FILTER_OPERATORS.IS_NULL) });
    }
    return ops;
  }

  // Numeric — "equals" too (MI-315): an amount is often looked up exactly.
  if (columnType === 'numeric') {
    const ops = filterDef.supportedOperators || ['gte', 'lte', 'gt', 'lt'];
    return ops
      .filter(op => [FILTER_OPERATORS.EQ, FILTER_OPERATORS.GT, FILTER_OPERATORS.LT, FILTER_OPERATORS.GTE, FILTER_OPERATORS.LTE].includes(op))
      .map(op => ({ value: op, label: tOp(op) }));
  }

  // Date / datetime
  if (columnType === 'date' || columnType === 'datetime') {
    const ops = filterDef.supportedOperators || ['gte', 'lte', 'gt', 'lt'];
    return ops
      .filter(op => [FILTER_OPERATORS.GT, FILTER_OPERATORS.LT, FILTER_OPERATORS.GTE, FILTER_OPERATORS.LTE].includes(op))
      .map(op => ({ value: op, label: tOp(op, true) }));
  }

  // Text (string without options)
  if (columnType === 'string' && !hasOptions) {
    const ops = filterDef.supportedOperators || ['ilike', 'not_ilike'];
    return ops
      .filter(op => Object.values(FILTER_OPERATORS).includes(op))
      .map(op => ({ value: op, label: tOp(op) }));
  }

  return [];
};

/**
 * Resolve a time preset to an ISO 8601 duration string (backend-resolved)
 * @param {Object} preset - { offsetValue, offsetUnit } e.g. { offsetValue: 1, offsetUnit: 'weeks' }
 * @returns {string} ISO 8601 duration string prefixed with "-" e.g. "-P1W", "-PT3H"
 */
export function resolveTimePreset(preset) {
  if (!preset || !preset.offsetValue || !preset.offsetUnit) return null;

  const { offsetValue, offsetUnit } = preset;

  const unitMap = {
    hours: `PT${offsetValue}H`,
    days: `P${offsetValue}D`,
    weeks: `P${offsetValue}W`,
    months: `P${offsetValue}M`,
    years: `P${offsetValue}Y`,
  };

  const duration = unitMap[offsetUnit];
  return duration ? `-${duration}` : null;
}
